import React, { useState, useEffect, useRef, useMemo } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import GroupMembersDropdown from "../../../../components/messaging/GroupMembersDropdown";
import { useBarOwnerData } from "../../../../context/BarOwnerDataContext";
import { useUserRole } from "../../../../context/UserRoleContext";
import artistGif from "../../../../assets/images/artist.gif";
import { artistGenresList } from "../../../../utils/genres";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "8px",
  marginTop: "10px",
};

const defaultCenter = {
  lat: 24.5551,
  lng: -81.78,
};

const ManagementArtistProfileModal = ({
  isOpen,
  onClose,
  artist,
  associations = [],
  selectedBar,
  isMapsLoaded = false,
  onRefresh,
  /** "all" = every bar the user owns or admins. "context" = only contextBarId (e.g. bar admin on Artists page). */
  manageBarSelection = "all",
  contextBarId,
}) => {
  const {
    getBars,
    getArtistMembers,
    fetchArtistMembers,
    invalidateCache,
    invalidateArtistMembers,
  } = useBarOwnerData();
  const { userData } = useUserRole();
  const messagesPath =
    userData?.role === "bar_admin"
      ? "/bar-admin/messages"
      : "/bar-owner/messages";

  const [showMessageDropdown, setShowMessageDropdown] = useState(false);
  const messageButtonRef = useRef(null);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [mapZoom, setMapZoom] = useState(13);
  const [saving, setSaving] = useState(false);
  // Manage Artist: selected bar ids (checked). Initialize from associations when modal opens. No API calls until Save.
  const [selectedBarIds, setSelectedBarIds] = useState(new Set());

  const artistId = artist?.artist_id;
  const { members, loading: membersLoading } = getArtistMembers(artistId);

  const isContextScope =
    manageBarSelection === "context" && contextBarId != null;

  const associationsForManage = useMemo(() => {
    const list = associations || [];
    if (!isContextScope) return list;
    return list.filter((a) => a.bar_id === contextBarId);
  }, [associations, isContextScope, contextBarId]);

  const allBars = getBars().bars || [];
  const bars = isContextScope
    ? allBars.filter((b) => b.bar_id === contextBarId)
    : allBars;

  // Stable key so we only sync when the actual set of bar ids in associations changes (not on every parent re-render with a new [] reference)
  const associationsBarIdsKey = useMemo(
    () =>
      associationsForManage
        .map((a) => a.bar_id)
        .sort()
        .join(","),
    [associationsForManage]
  );

  // Initial bar ids (from server) - used to detect changes and highlight modified rows
  const initialBarIds = useMemo(
    () => new Set(associationsForManage.map((a) => a.bar_id)),
    [associationsBarIdsKey]
  );

  const hasChanges =
    selectedBarIds.size !== initialBarIds.size ||
    [...selectedBarIds].some((id) => !initialBarIds.has(id)) ||
    [...initialBarIds].some((id) => !selectedBarIds.has(id));

  // Sync selectedBarIds from associations when modal opens or associations content change
  useEffect(() => {
    if (!isOpen || !artistId) return;
    setSelectedBarIds(new Set(associationsForManage.map((a) => a.bar_id)));
  }, [isOpen, artistId, associationsBarIdsKey]);

  // Ensure members are loaded when modal opens (fetch on open if not in cache)
  useEffect(() => {
    if (!isOpen || !artistId) return;
    const cached = getArtistMembers(artistId);
    if (!cached.hasFetched && !cached.loading) {
      fetchArtistMembers(artistId);
    }
  }, [isOpen, artistId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set map center and marker from artist coordinates
  useEffect(() => {
    if (isOpen && artist) {
      const lat = artist.latitude != null ? parseFloat(artist.latitude) : null;
      const lng =
        artist.longitude != null ? parseFloat(artist.longitude) : null;
      if (
        lat != null &&
        lng != null &&
        !isNaN(lat) &&
        !isNaN(lng) &&
        (lat !== 0 || lng !== 0)
      ) {
        const center = { lat, lng };
        setMapCenter(center);
        setMarkerPosition(center);
        setMapZoom(14);
      } else {
        setMapCenter(defaultCenter);
        setMarkerPosition(null);
        setMapZoom(13);
      }
    }
  }, [isOpen, artist]);

  useEffect(() => {
    if (!isOpen) {
      setMapExpanded(false);
      setMapCenter(defaultCenter);
      setMarkerPosition(null);
      setMapZoom(13);
      setShowMessageDropdown(false);
    }
  }, [isOpen]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const activeMembers = (members || []).filter(
    (m) => m.status !== "pending" && m.user_id
  );
  const memberCount = activeMembers.length;
  const genreList = artist ? artistGenresList(artist) : [];

  const getAssocForBar = (barId) =>
    associationsForManage.find((a) => a.bar_id === barId);

  // Only update local state; all add/remove/cancel happens on Save
  const handleBarCheckChange = (barId, checked) => {
    setSelectedBarIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(barId);
      else next.delete(barId);
      return next;
    });
  };

  const handleSelectAllBars = () => {
    setSelectedBarIds(new Set(bars.map((b) => b.bar_id)));
  };

  const handleDeselectAllBars = () => {
    setSelectedBarIds(new Set());
  };

  const handleManageArtistSave = async () => {
    const addBarIds = bars
      .map((b) => b.bar_id)
      .filter((id) => selectedBarIds.has(id) && !getAssocForBar(id));
    const removeBarIds = associationsForManage
      .map((a) => a.bar_id)
      .filter((id) => !selectedBarIds.has(id));

    if (addBarIds.length === 0 && removeBarIds.length === 0) return;

    setSaving(true);
    try {
      const response = await fetch(`/api/artists/${artistId}/bars`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ addBarIds, removeBarIds }),
      });
      const data = await response.json();
      if (data.success) {
        invalidateCache("managementArtists");
        if (typeof onRefresh === "function") onRefresh();
        invalidateArtistMembers(artistId);
        if (
          removeBarIds.length > 0 &&
          associationsForManage.length === removeBarIds.length
        ) {
          onClose();
        }
        if (addBarIds.length > 0) onClose();
      } else {
        alert(data.error || "Failed to update bars");
      }
    } catch (err) {
      console.error("Error updating artist bars:", err);
      alert("Failed to update bars");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !artist) return null;

  return (
    <div
      className="artist-profile-modal-overlay"
      role="presentation"
      onClick={handleOverlayClick}
    >
      <div
        className="artist-profile-modal groups-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="management-artist-profile-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="artist-profile-modal-header">
          <h2 id="management-artist-profile-modal-title">
            {artist.stage_name || "Artist"}
          </h2>
          <div className="artist-profile-modal-header-actions">
            {!membersLoading && memberCount > 0 && (
              <button
                ref={messageButtonRef}
                type="button"
                className="artist-profile-message-btn"
                onClick={() => setShowMessageDropdown(true)}
                aria-label="Message artist members"
              >
                💬
              </button>
            )}
            <button
              type="button"
              className="artist-profile-modal-close"
              aria-label="Close"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        {showMessageDropdown && (
          <GroupMembersDropdown
            isOpen={showMessageDropdown}
            onClose={() => setShowMessageDropdown(false)}
            members={activeMembers}
            buttonRef={messageButtonRef}
            messagesPath={messagesPath}
          />
        )}

        <div className="artist-profile-modal-body groups-modal-body">
          {membersLoading ? (
            <div className="groups-modal-loading groups-modal-loading--centered">
              <span className="spinner" aria-hidden="true" />
              <span>Loading…</span>
            </div>
          ) : (
            <>
              <div className="onboarding-section">
                <h2 className="section-title">Artist Information</h2>
                <div className="groups-modal-info-section">
                  <div className="groups-modal-photo-wrapper">
                    <img
                      src={artist.artist_pic || artistGif}
                      alt={artist.stage_name || "Artist"}
                      className="groups-modal-photo"
                      onError={(e) => {
                        e.currentTarget.src = artistGif;
                      }}
                    />
                  </div>
                  <div className="groups-modal-info-fields">
                    <div className="form-grid">
                      {artist.type && (
                        <div className="input-group">
                          <span className="input-label">Type</span>
                          <div className="display-value">{artist.type}</div>
                        </div>
                      )}
                      <div className="input-group">
                        <span className="input-label">Member Count</span>
                        <div className="display-value">{memberCount}</div>
                      </div>
                      {genreList.length > 0 && (
                        <div className="input-group">
                          <span className="input-label">Genres</span>
                          <div className="display-value">
                            <div className="artist-genre-pills">
                              {genreList.map((g) => (
                                <span key={g} className="artist-genre-pill">
                                  {g}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      {artist.hometown && (
                        <div className="input-group">
                          <span className="input-label">Hometown</span>
                          <div className="display-value">{artist.hometown}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="onboarding-section">
                <h2 className="section-title">Description</h2>
                {artist.description ? (
                  <p className="groups-modal-description">
                    {artist.description}
                  </p>
                ) : null}
              </div>

              <div className="onboarding-section">
                <h2 className="section-title">Members</h2>
                {members.length > 0 ? (
                  <div className="groups-modal-members">
                    {members.map((member) => (
                      <div
                        key={member.user_id || member.invite_id || member.email}
                        className="groups-modal-member-tile"
                      >
                        <div className="groups-modal-member-info">
                          <span className="groups-modal-member-name">
                            {member.name || member.email}
                          </span>
                          {member.status === "pending" && (
                            <span className="groups-modal-member-badge pending">
                              Pending
                            </span>
                          )}
                          {member.role === "owner" && (
                            <span className="groups-modal-member-role">
                              Owner
                            </span>
                          )}
                          {member.role === "member" &&
                            member.status !== "pending" && (
                              <span className="groups-modal-member-role">
                                Member
                              </span>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="groups-modal-empty">No members found</p>
                )}
              </div>

              <div className="onboarding-section">
                <h2 className="section-title">Socials</h2>
                <div className="form-grid">
                  {artist.facebook && (
                    <div className="input-group">
                      <span className="input-label">Facebook</span>
                      <div className="display-value">
                        <a
                          href={
                            artist.facebook.startsWith("http")
                              ? artist.facebook
                              : `https://facebook.com/${artist.facebook}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="groups-modal-social-link"
                        >
                          {artist.facebook}
                        </a>
                      </div>
                    </div>
                  )}
                  {artist.instagram && (
                    <div className="input-group">
                      <span className="input-label">Instagram</span>
                      <div className="display-value">
                        <a
                          href={
                            artist.instagram.startsWith("http")
                              ? artist.instagram
                              : `https://instagram.com/${artist.instagram}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="groups-modal-social-link"
                        >
                          {artist.instagram}
                        </a>
                      </div>
                    </div>
                  )}
                  {artist.website && (
                    <div className="input-group">
                      <span className="input-label">Website</span>
                      <div className="display-value">
                        <a
                          href={
                            artist.website.startsWith("http")
                              ? artist.website
                              : `https://${artist.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="groups-modal-social-link"
                        >
                          {artist.website}
                        </a>
                      </div>
                    </div>
                  )}
                  {artist.venmo && (
                    <div className="input-group">
                      <span className="input-label">Venmo</span>
                      <div className="display-value">{artist.venmo}</div>
                    </div>
                  )}
                </div>
              </div>

              {artist.latitude != null && artist.longitude != null && (
                <div className="onboarding-section">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: mapExpanded ? "1rem" : "0",
                    }}
                  >
                    <h2 className="section-title">Location</h2>
                    <button
                      type="button"
                      onClick={() => setMapExpanded(!mapExpanded)}
                      className="bar-modal-map-toggle"
                      aria-label={mapExpanded ? "Collapse map" : "Expand map"}
                    >
                      {mapExpanded ? "Hide Map" : "Show Map"}
                    </button>
                  </div>
                  {mapExpanded && (
                    <div
                      className="input-group full-width"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isMapsLoaded ? (
                        <div onClick={(e) => e.stopPropagation()}>
                          <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={mapCenter}
                            zoom={mapZoom}
                            options={{ gestureHandling: "cooperative" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {markerPosition && (
                              <Marker
                                position={markerPosition}
                                draggable={false}
                              />
                            )}
                          </GoogleMap>
                        </div>
                      ) : (
                        <div
                          style={{
                            ...mapContainerStyle,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f0f0f0",
                            color: "#666",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Loading map...
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="onboarding-section">
                <h2 className="section-title">
                  {associationsForManage.length === 0
                    ? "Invite Artist"
                    : "Manage Artist"}
                </h2>
                <p className="input-hint" style={{ marginBottom: "0.75rem" }}>
                  {isContextScope
                    ? associationsForManage.length === 0
                      ? "Invite this artist to the bar selected in the sidebar, or cancel."
                      : "Add or remove this artist from the bar selected in the sidebar. Changes apply when you click Save."
                    : associationsForManage.length === 0
                    ? "Select which bars you want to invite this artist to."
                    : "Select which bars this artist is associated with. Changes are applied when you click Save."}
                </p>
                {!(isContextScope && bars.length <= 1) ? (
                  <div
                    className="bar-select-actions"
                    style={{ marginBottom: "0.75rem" }}
                  >
                    <button
                      type="button"
                      className="bar-select-btn"
                      onClick={handleSelectAllBars}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="bar-select-btn"
                      onClick={handleDeselectAllBars}
                    >
                      Deselect all
                    </button>
                  </div>
                ) : null}
                <div className="manage-artist-bar-list">
                  {bars.length === 0 ? (
                    <p className="groups-modal-empty">No bars found</p>
                  ) : (
                    bars.map((bar) => {
                      const assoc = getAssocForBar(bar.bar_id);
                      const checked = selectedBarIds.has(bar.bar_id);
                      const isPending = assoc?.status === "pending";
                      const wasInitiallySelected = initialBarIds.has(bar.bar_id);
                      const isChanged = checked !== wasInitiallySelected;
                      const buttonLabel = !checked
                        ? "Add To"
                        : isPending
                        ? "Cancel"
                        : "Remove From";
                      return (
                        <div
                          key={bar.bar_id}
                          className={`manage-artist-bar-row${isChanged ? " manage-artist-bar-row--changed" : ""}`}
                        >
                          <span className="manage-artist-bar-name">
                            {bar.name}
                          </span>
                          <div className="manage-artist-bar-right">
                            {assoc && isPending && (
                              <span className="manage-artist-bar-pending">
                                Pending
                              </span>
                            )}
                            <button
                            type="button"
                            className={`manage-artist-bar-btn manage-artist-bar-btn--${!checked ? "add" : isPending ? "cancel" : "remove"}`}
                            onClick={() =>
                              handleBarCheckChange(bar.bar_id, !checked)
                            }
                          >
                              {buttonLabel}
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <button
                  type="button"
                  className="artist-profile-invite-btn"
                  disabled={saving || !hasChanges}
                  onClick={handleManageArtistSave}
                  style={{ marginTop: "0.75rem" }}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagementArtistProfileModal;
